import { useCallback, useMemo } from 'react';

import type { SportMonetizationCurrentEvent } from '../../../dto/monetization-events.types';
import { SportEventType } from '../../../dto/sport.types';
import { useStores } from '../../../stores/useStores';

interface UseMonetizationWidgetDataProps {
  event: SportMonetizationCurrentEvent;
}

const determineSubType = (gameNameType: string) => {
  switch (gameNameType) {
    case SportEventType.GAME_OF_THE_DAY:
      return SportEventType.GAME_OF_THE_DAY;
    case SportEventType.TOTO:
      return SportEventType.TOTO;
    default:
      return SportEventType.GAME_OF_THE_DAY;
  }
};

export function useMonetizationWidgetData(props: UseMonetizationWidgetDataProps) {
  const { event } = props;
  const { widgetImageUrl, metadata } = event;
  const { sportEventsStore, monetizationEventsStore } = useStores();

  const { status, startDate } = metadata ?? {};

  const analyticsParams = useMemo(
    () =>
      ({
        event_name: 'widget',
        feature_type: 'crown_sports',
        feature_sub_value: 'crown_sports',
        click_location: 'widget',
        monetization_event_id: event.eventId,
        feature_sub_type: determineSubType(event.gameNameType),
      }) as const,
    [event.gameNameType, event.eventId],
  );

  const matchStatus = useMemo(() => {
    if (!status) return null;
    return {
      isStarted: status === 'STARTED',
      isUpcoming: status === 'UPCOMING',
      isEnded: status === 'ENDED',
    };
  }, [status]);

  const timerDate = useMemo(() => {
    if (!startDate || !event?.endDate) {
      return null;
    }
    if (matchStatus?.isUpcoming) {
      return new Date(startDate);
    }
    if (matchStatus?.isStarted) {
      return new Date(event.endDate);
    }
    return null;
  }, [startDate, event.endDate, matchStatus?.isUpcoming, matchStatus?.isStarted]);

  const isClickable = !!matchStatus && !matchStatus.isEnded;

  const handleClick = useCallback(() => {
    if (!isClickable) return;
    void sportEventsStore.startSportEvent();
    void monetizationEventsStore.sendAnalyticsEvent({ ...analyticsParams, event_action: 'click' });
  }, [analyticsParams, isClickable, monetizationEventsStore, sportEventsStore]);

  const onTimeEnd = useCallback(() => {
    void monetizationEventsStore.fetch();
  }, [monetizationEventsStore]);

  return { timerDate, widgetImageUrl, handleClick, onTimeEnd, isClickable };
}
